'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { QrCode, X, Camera } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { colors } from '@/styles/theme';
import { toast } from '@/lib/toast';
import { useTranslation } from '@/hooks/useTranslation';
import { interpolate } from './i18n';
import { robotApi } from './api';

/**
 * QR 扫码触发按钮 — 现场扫机器人身上的二维码，跳到该 robot 详情页。
 * 两种扫码方式：
 *   1) 内置摄像头（html5-qrcode）— PC 端 / 办公室
 *   2) 手输 ffsn 兜底
 *   3) 手机端通常用 OS 原生相机扫，扫的是 URL `/robot-manager/r/<ffsn>` 直接打开浏览器跳转
 */
export function QRScanButton() {
  const { t } = useTranslation();
  const rm = t.robotManager;
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <QrCode className="w-4 h-4 mr-1" />
        {rm.qr.scan}
      </Button>
      <QRScanDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function QRScanDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const rm = t.robotManager;
  const router = useRouter();
  const containerRef = useRef<HTMLDivElement>(null);
  const scannerRef = useRef<any>(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [ffsnInput, setFfsnInput] = useState('');

  // 解析扫到的内容 — 可能是完整 URL 或裸 ffsn
  const resolveAndNavigate = async (raw: string) => {
    const text = raw.trim();
    if (!text) return;
    // URL 形式：/robot-manager/r/FF-202605-00123 或 https://.../r/FF-202605-00123
    const urlMatch = text.match(/\/robot-manager\/r\/([^/?#]+)/);
    const ffsn = urlMatch ? decodeURIComponent(urlMatch[1]) : text;
    try {
      const { id } = await robotApi.getByFfsn(ffsn);
      onClose();
      router.push(`/robot-manager/${id}`);
    } catch {
      toast.error(interpolate(rm.qr.notFound, { ffsn }));
    }
  };

  useEffect(() => {
    if (!open || !cameraOn) return;
    let cancelled = false;
    (async () => {
      const { Html5Qrcode } = await import('html5-qrcode');
      if (cancelled || !containerRef.current) return;
      const scanner = new Html5Qrcode(containerRef.current.id);
      scannerRef.current = scanner;
      try {
        await scanner.start(
          { facingMode: 'environment' }, // 后置摄像头
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decoded: string) => {
            scanner.stop().catch(() => {});
            resolveAndNavigate(decoded);
          },
          () => {}, // ignore frame fail
        );
        // 用户在 start() 异步期间关掉了 dialog —— 立刻停掉，避免摄像头灯常亮
        if (cancelled) {
          await scanner.stop().catch(() => {});
          scanner.clear();
          scannerRef.current = null;
        }
      } catch (err: any) {
        toast.error(err?.message ?? rm.qr.cameraError);
        setCameraOn(false);
      }
    })();
    return () => {
      cancelled = true;
      if (scannerRef.current) {
        scannerRef.current
          .stop()
          .then(() => scannerRef.current?.clear())
          .catch(() => {});
        scannerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cameraOn]);

  useEffect(() => {
    if (!open) setCameraOn(false);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <QrCode className="w-4 h-4 mr-2" />
            {rm.qr.scanTitle}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* 摄像头扫码 */}
          <div>
            {!cameraOn ? (
              <Button variant="outline" className="w-full" onClick={() => setCameraOn(true)}>
                <Camera className="w-4 h-4 mr-2" />
                {rm.qr.startCamera}
              </Button>
            ) : (
              <div className="space-y-2">
                <div
                  id="rm-qr-scanner-region"
                  ref={containerRef}
                  className="w-full rounded overflow-hidden"
                  style={{ minHeight: 240, backgroundColor: colors.bgSecondary }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setCameraOn(false)}
                >
                  <X className="w-4 h-4 mr-1" />
                  {rm.qr.closeCamera}
                </Button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <div className="flex-1 h-px" style={{ backgroundColor: colors.border }} />
            <span className="text-xs" style={{ color: colors.textTertiary }}>
              {rm.qr.orManual}
            </span>
            <div className="flex-1 h-px" style={{ backgroundColor: colors.border }} />
          </div>

          {/* 手输 ffsn 兜底 */}
          <div className="flex gap-2">
            <Input
              placeholder="FF-202605-00123"
              value={ffsnInput}
              onChange={(e) => setFfsnInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && resolveAndNavigate(ffsnInput)}
            />
            <Button size="sm" onClick={() => resolveAndNavigate(ffsnInput)}>
              {rm.qr.open}
            </Button>
          </div>

          <p className="text-xs leading-relaxed" style={{ color: colors.textTertiary }}>
            {rm.qr.mobileHint}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * 在详情页展示当前机器人的二维码 — 现场可打印
 */
export function QRDisplay({ ffsn }: { ffsn: string }) {
  const { t } = useTranslation();
  const rm = t.robotManager;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const url = typeof window !== 'undefined'
    ? `${window.location.origin}/robot-manager/r/${encodeURIComponent(ffsn)}`
    : '';

  useEffect(() => {
    if (!canvasRef.current || !ffsn) return;
    let cancelled = false;
    (async () => {
      const QRCode = (await import('qrcode')).default;
      if (cancelled || !canvasRef.current) return;
      await QRCode.toCanvas(canvasRef.current, url, { width: 160, margin: 1 });
    })();
    return () => {
      cancelled = true;
    };
  }, [ffsn, url]);

  return (
    <div className="flex flex-col items-center gap-2 p-3 rounded border" style={{ borderColor: colors.border }}>
      <canvas ref={canvasRef} />
      <div className="text-xs font-mono" style={{ color: colors.textSecondary }}>
        {ffsn}
      </div>
      <div className="text-[10px] text-center leading-tight" style={{ color: colors.textTertiary }}>
        {rm.qr.scanOrVisit}<br />
        <code className="text-[10px]">{url.replace(/^https?:\/\//, '')}</code>
      </div>
    </div>
  );
}
